import { motion } from 'framer-motion';
import { Phone, CheckCircle, Globe, Layout, ShoppingCart, Rocket, Zap, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FAQ } from '../components/FAQ';
import { Contact } from '../components/Contact';

const WHATSAPP_NUMBER = "5521980277322";

const services = [
  "Landing Pages de alta conversão",
  "Sites institucionais",
  "Lojas virtuais (E-commerce)",
  "Blogs e portais",
  "Sites responsivos (mobile-first)",
  "Otimização SEO",
  "Integração com redes sociais",
  "Formulários e automações",
  "Hospedagem e domínio",
  "Manutenção mensal",
];

const packages = [
  {
    name: "Landing Page",
    price: "A partir de R$ 997",
    features: ["1 página de alta conversão", "Otimizado para Google", "Formulário WhatsApp", "Design personalizado", "Entrega em 7 dias"],
    highlight: false,
  },
  {
    name: "Site Institucional",
    price: "A partir de R$ 1.997",
    features: ["Até 5 páginas", "Blog integrado", "SEO completo", "Painel administrativo", "Suporte por 30 dias"],
    highlight: true,
  },
  {
    name: "E-commerce",
    price: "A partir de R$ 3.997",
    features: ["Loja virtual completa", "Integração de pagamentos", "Gestão de produtos", "Dashboard de vendas", "Treinamento incluso"],
    highlight: false,
  },
];

const benefits = [
  { icon: Rocket, title: "Sites Rápidos", desc: "Páginas otimizadas para carregamento ultrarrápido." },
  { icon: Zap, title: "Alta Conversão", desc: "Design focado em transformar visitantes em clientes." },
  { icon: Search, title: "SEO Otimizado", desc: "Apareça no Google e atraia tráfego orgânico." },
];

const WebDesignPage = () => {
  return (
    <div data-testid="webdesign-page">
      {/* Hero Section */}
      <section className="hero-bg min-h-[60vh] flex items-center pt-32 md:pt-40 pb-16">
        <div className="hero-content max-w-7xl mx-auto px-6 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <Link to="/" className="text-[#00F0FF] text-sm font-mono uppercase tracking-widest hover:underline mb-4 inline-block">
              ← Voltar para Início
            </Link>
            
            <div className="inline-block mb-6">
              <span className="text-[#00F0FF] text-sm font-mono uppercase tracking-widest border border-[#00F0FF]/30 px-4 py-2 bg-[#00F0FF]/5">
                [ Criação de Sites ]
              </span>
            </div>
            
            <h1 className="font-orbitron text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Criação de <span className="text-[#00F0FF] neon-text">Sites</span>
              <br />Profissionais
            </h1>
            
            <p className="text-[#94A3B8] text-lg md:text-xl font-space leading-relaxed mb-8">
              Desenvolvimento de sites modernos, responsivos e otimizados para conversão. 
              Landing pages, sites institucionais e lojas virtuais para seu negócio crescer.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <motion.a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Tenho interesse em criar um site profissional para meu negócio.`}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="whatsapp-btn justify-center text-base"
                data-testid="webdesign-whatsapp-btn"
              >
                <Phone size={20} />
                Solicitar Orçamento
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-dark py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white text-center mb-12">
            Sites que <span className="text-[#00F0FF]">Convertem</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="cyber-card p-8 text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 border border-[#00F0FF]/30 flex items-center justify-center bg-[#00F0FF]/5">
                  <item.icon size={28} className="text-[#00F0FF]" />
                </div>
                <h3 className="font-rajdhani font-bold text-white text-xl mb-3">{item.title}</h3>
                <p className="text-sm text-[#94A3B8] font-space">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="section-darker py-20 grid-bg">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-[#00F0FF] text-sm font-mono uppercase tracking-widest">
              [ Pacotes ]
            </span>
            <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white mt-4">
              Escolha o <span className="text-[#00F0FF]">Plano Ideal</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`cyber-card p-8 ${pkg.highlight ? 'border-[#00F0FF] shadow-[0_0_30px_rgba(0,240,255,0.2)]' : ''}`}
              >
                {pkg.highlight && (
                  <div className="text-center mb-4">
                    <span className="text-xs font-mono bg-[#00F0FF] text-black px-3 py-1">
                      MAIS POPULAR
                    </span>
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="font-rajdhani font-bold text-white text-2xl mb-2">{pkg.name}</h3>
                  <div className="font-orbitron text-3xl font-bold text-[#00F0FF]">{pkg.price}</div>
                </div>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <CheckCircle size={16} className="text-[#00F0FF] flex-shrink-0" />
                      <span className="text-sm text-[#94A3B8] font-space">{feature}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Tenho interesse no pacote ${pkg.name} para criação de site.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block text-center ${pkg.highlight ? 'whatsapp-btn justify-center' : 'cyber-btn'}`}
                  data-testid={`package-cta-${index}`}
                >
                  Solicitar Orçamento
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-dark py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white mb-8">
                Nossos <span className="text-[#00F0FF]">Serviços</span>
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {services.map((service, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle size={18} className="text-[#00F0FF] flex-shrink-0" />
                    <span className="text-sm text-[#94A3B8] font-space">{service}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex justify-center"
            >
              <div className="cyber-card p-8 max-w-md border-[#00F0FF]/30">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 border border-[#00F0FF]/30 flex items-center justify-center bg-[#00F0FF]/5">
                    <Globe size={32} className="text-[#00F0FF]" />
                  </div>
                  <div>
                    <h3 className="font-rajdhani font-bold text-white text-xl">Parceiro Emergent</h3>
                    <p className="text-sm text-[#94A3B8]">Tecnologia de ponta</p>
                  </div>
                </div>
                <p className="text-[#94A3B8] font-space text-sm leading-relaxed mb-6">
                  Utilizamos as mais modernas tecnologias do mercado para criar sites rápidos, 
                  seguros e otimizados. Desenvolvimento com React, Next.js e as melhores práticas.
                </p>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Quero saber mais sobre os serviços de criação de sites.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cyber-btn w-full text-center block"
                  data-testid="tech-cta-btn"
                >
                  Falar com Especialista
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <FAQ />
      <Contact />
    </div>
  );
};

export default WebDesignPage;
